import {graphql} from 'gatsby'
import React from 'react'
import styled, {css} from 'styled-components'

import {pricingTooltipActioned} from '@/analytics/segment'
import Button from '@/components/button'
import {Text, text} from '@/components/text'
import {InfoTooltip} from '@/components/tooltip/info-tooltip'
import {VisuallyHidden} from '@/components/visually-hidden'
import {usePricingCtas} from '@/hooks/use-pricing-ctas'
import checkIconPink from '@/images/icons/check-pink.svg'
import {colors, tokens} from '@/theme'
import getEnvironmentFormConfig from '@/utils/get-environment-field'
import mq from '@/utils/media-query'

import type {ButtonVariant} from '@/components/button'
import type {PricingFragment} from '@/graphql-types'
import type {FormConfig} from '@/types/consents'

interface PricingProps {
    content: PricingFragment['data']
    mode: BillingCycle
    rawDemoPath?: string
    withTooltips?: boolean
}

export const Tiers = ['starter', 'essential', 'advanced', 'beyond'] as const

// eslint-disable-next-line prettier/prettier
export type TierTypes = typeof Tiers[number]

export enum BillingCycle {
    Monthly = 'monthly',
    Yearly = 'yearly',
}

const PricingTierChildrenCount = 8

export const Pricing = ({
    content,
    withTooltips,
    mode = BillingCycle.Monthly,
    rawDemoPath,
}: PricingProps) => {
    const formConfig = getEnvironmentFormConfig({
        production: content.form_production,
        staging: content.form_staging,
    })

    const tiers = content.pricing_tiers.flatMap(({pricing_tier}, i) => ({
        ...pricing_tier.document.data,
        tierName: Tiers[i],
    }))

    const handleClick = usePricingCtas({
        formConfig: formConfig as FormConfig,
        signupModalFree: content.signup_modal_free,
        signupModalPro: content.signup_modal_pro,
        rawDemoPath,
    })

    const isMonthlyCycled = mode === BillingCycle.Monthly

    return (
        <Wrapper $columns={tiers.length}>
            {tiers.map(
                (
                    {
                        monthly_cost,
                        annual_cost,
                        name,
                        monthly_billing_screen_reader_alt,
                        annual_billing_screen_reader_alt,
                        per_month_text,
                        cost_subtext,
                        monthly_cost_subtext_extra,
                        annual_cost_subtext_extra,
                        excerpt,
                        cta_text,
                        features,
                        features_title,
                        tierName,
                        footnote,
                        cta_variant,
                    },
                    index,
                ) => {
                    const billingCycleCost = isMonthlyCycled ? monthly_cost : annual_cost
                    const isStarter = name.toLowerCase().includes('starter')
                    const allowPriceTranslation = isStarter ? 'yes' : 'no'

                    return (
                        <Tier key={index}>
                            <TierName
                                as="h3"
                                $variant="heading-base"
                                style={{marginBottom: tokens.spacing12}}
                            >
                                {name}
                            </TierName>

                            <AmountText>
                                {!isMonthlyCycled && !isStarter && (
                                    <>
                                        <VisuallyHidden>
                                            {monthly_billing_screen_reader_alt}
                                        </VisuallyHidden>
                                        <Text
                                            as="del"
                                            $color="extraMuted"
                                            $variant="paragraph-large"
                                            translate={allowPriceTranslation}
                                        >
                                            {monthly_cost}
                                        </Text>{' '}
                                    </>
                                )}
                                <VisuallyHidden>{annual_billing_screen_reader_alt}</VisuallyHidden>{' '}
                                <Text
                                    as="ins"
                                    $color="muted"
                                    $variant="paragraph-large"
                                    translate={allowPriceTranslation}
                                >
                                    {billingCycleCost}
                                </Text>{' '}
                                <Text as="span" $color="muted" $variant="paragraph-large">
                                    {per_month_text}
                                </Text>
                            </AmountText>

                            <div>
                                <Text $variant="label-base" style={{marginBottom: tokens.spacing4}}>
                                    {cost_subtext}
                                </Text>

                                <Text $variant="label-base">
                                    {isMonthlyCycled
                                        ? monthly_cost_subtext_extra
                                        : annual_cost_subtext_extra}
                                </Text>
                            </div>

                            <Excerpt $variant="paragraph-base" $color="lightMuted">
                                {excerpt}
                            </Excerpt>

                            {content.show_ctas && (
                                <StyledButton
                                    data-testid={`pricing-tier-cta-${name}`}
                                    onClick={() => handleClick(tierName)}
                                    $variant={
                                        cta_variant ? (cta_variant as ButtonVariant) : 'default'
                                    }
                                >
                                    {cta_text}
                                </StyledButton>
                            )}

                            <FeaturesTitle $variant="paragraph-large">
                                {features_title}
                            </FeaturesTitle>

                            <FeaturesList>
                                {features.map(
                                    ({feature, disabled_monthly, is_highlighted, tooltip_text}) => (
                                        <FeaturesItem
                                            $tooltipText={withTooltips ? tooltip_text : undefined}
                                            $isHighlighted={
                                                is_highlighted ||
                                                (disabled_monthly && !isMonthlyCycled)
                                            }
                                            $isDisabled={disabled_monthly && isMonthlyCycled}
                                            key={feature}
                                        >
                                            {withTooltips ? feature.replace(/\*/g, '') : feature}
                                            {withTooltips && tooltip_text && (
                                                <InfoTooltip
                                                    label={tooltip_text}
                                                    onMouseEnter={() =>
                                                        pricingTooltipActioned({
                                                            feature,
                                                        })
                                                    }
                                                />
                                            )}
                                        </FeaturesItem>
                                    ),
                                )}
                            </FeaturesList>

                            {footnote && !withTooltips && (
                                <Footnote $variant="label-base" $color="brand">
                                    {footnote}
                                </Footnote>
                            )}
                        </Tier>
                    )
                },
            )}
        </Wrapper>
    )
}

const Wrapper = styled.div<{$columns: number}>`
    display: grid;
    grid-template-columns: repeat(1, 1fr);
    grid-template-rows: repeat(${PricingTierChildrenCount}, auto);
    row-gap: ${tokens.spacing20};

    ${mq.min('sm')} {
        column-gap: ${tokens.spacing20};
        grid-template-columns: repeat(2, 1fr);
    }

    ${mq.min('md')} {
        row-gap: 0;
        column-gap: ${tokens.spacing2};
        grid-template-columns: repeat(${(props) => props.$columns}, 1fr);
    }

    ${mq.min('lg')} {
        column-gap: ${tokens.spacing20};
    }
`

const Tier = styled.div`
    display: grid;
    grid-template-rows: subgrid;
    grid-row: span ${PricingTierChildrenCount};
    background-color: ${colors.background.default};
    padding: ${tokens.spacing20};
    row-gap: 0;
`
const TierName = styled(Text)`
    ${mq.min('md')} {
        ${text({$variant: 'title-base'})}
    }
`

const Excerpt = styled(Text)`
    margin-top: ${tokens.spacing32};
    margin-bottom: ${tokens.spacing32};
`

const StyledButton = styled(Button)`
    margin-bottom: ${tokens.spacing32};
    width: 100%;

    ${mq.min('sm')} {
        width: max-content;
    }
`

const FeaturesTitle = styled(Text)`
    ${mq.min('md')} {
        ${text({$variant: 'paragraph-base'})}
    }
`

const FeaturesList = styled.ul`
    display: flex;
    flex-direction: column;
    gap: ${tokens.spacing20};
    padding: 0;
    margin-top: ${tokens.spacing20};
`

const AmountText = styled.div`
    ins {
        text-decoration: none;
    }

    ${mq.min('sm')} {
        margin-bottom: ${tokens.spacing8};
    }
`

interface FeaturesItemProps {
    $isHighlighted: boolean
    $isDisabled: boolean
    $tooltipText?: string
}

const FeaturesItem = styled.li<FeaturesItemProps>(({$tooltipText, $isHighlighted, $isDisabled}) => {
    const highlightedStyles = css`
        color: ${colors.text.brand};
    `

    const disabledStyles = css`
        color: ${colors.text.extraMuted};
        text-decoration: line-through;

        /* This combination's outcome is #CCC */
        &::before {
            filter: grayscale(1);
            opacity: 0.42;
        }
    `

    const checkIconStyles = css`
        &::before {
            margin-right: ${tokens.spacing12};
            content: url(${checkIconPink});
        }
    `

    const tooltipStyles = css`
        justify-content: space-between;
    `

    return css`
        display: flex;

        ${text({$variant: 'paragraph-base', $color: 'muted'})}

        ${$isHighlighted && highlightedStyles}
        ${$isDisabled && disabledStyles}
        ${$tooltipText ? tooltipStyles : checkIconStyles}
    `
})

const Footnote = styled(Text)`
    margin-top: ${tokens.spacing12};

    &::before {
        content: '*';
    }
`

export const pricingFragment = graphql`
    fragment Pricing on PrismicPricingTiers {
        lang
        data {
            title_image {
                alt
                copyright
                url
                dimensions {
                    height
                    width
                }
            }
            billing_cycle {
                billed_monthly_label
                billed_yearly_label
                description
                legend
            }
            form_production {
                formId
                consents {
                    type
                    communicationConsents {
                        id
                        required
                    }
                }
            }
            form_staging {
                formId
                consents {
                    type
                    communicationConsents {
                        id
                        required
                    }
                }
            }
            signup_modal_free {
                image {
                    fluid {
                        base64
                    }
                }
                title
                subtitle
                highlighted_feature_1
                highlighted_feature_2
                highlighted_feature_3
            }
            signup_modal_pro {
                image {
                    fluid {
                        base64
                    }
                }
                title
                subtitle
                highlighted_feature_1
                highlighted_feature_2
                highlighted_feature_3
            }
            show_ctas
            slider_label
            slider_tooltip_text
            slider_sub_text {
                richText
            }
            billing_monthly_label_v2
            billing_yearly_label_v2
        }
        ...PricingTiersFragment
    }

    fragment PricingTiersFragment on PrismicPricingTiers {
        lang
        data {
            pricing_tiers {
                pricing_tier {
                    ... on PrismicLinkType {
                        document {
                            ... on PrismicPricingTier {
                                data {
                                    name
                                    per_month_text
                                    cost_subtext
                                    excerpt
                                    cta_text
                                    features_title
                                    features {
                                        feature
                                        disabled_monthly
                                        is_highlighted
                                        tooltip_text
                                    }
                                    annual_cost
                                    monthly_cost
                                    monthly_billing_screen_reader_alt
                                    annual_billing_screen_reader_alt
                                    monthly_cost_subtext_extra
                                    annual_cost_subtext_extra
                                    footnote
                                    monthly_billed_cost
                                    annually_billed_cost
                                    monthly_billed_user_cost
                                    annually_billed_user_cost
                                    cta_variant
                                    free_users_included
                                    cashback_text {
                                        richText
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }

    fragment SecondaryPricing on PrismicSecondaryPricingTiers {
        lang
        data {
            locale_code
            title_image {
                alt
                copyright
                url
                dimensions {
                    height
                    width
                }
            }
            billing_cycle {
                billed_monthly_label
                billed_yearly_label
                description
                legend
            }
            form_production {
                formId
                consents {
                    type
                    communicationConsents {
                        id
                        required
                    }
                }
            }
            form_staging {
                formId
                consents {
                    type
                    communicationConsents {
                        id
                        required
                    }
                }
            }
            signup_modal_free {
                image {
                    fluid {
                        base64
                    }
                }
                title
                subtitle
                highlighted_feature_1
                highlighted_feature_2
                highlighted_feature_3
            }
            signup_modal_pro {
                image {
                    fluid {
                        base64
                    }
                }
                title
                subtitle
                highlighted_feature_1
                highlighted_feature_2
                highlighted_feature_3
            }
            show_ctas
            slider_label
            slider_tooltip_text
            slider_sub_text {
                richText
            }
            billing_monthly_label_v2
            billing_yearly_label_v2
            pricing_tiers {
                pricing_tier {
                    ... on PrismicLinkType {
                        document {
                            ... on PrismicSecondaryPricingTier {
                                data {
                                    name
                                    per_month_text
                                    cost_subtext
                                    excerpt
                                    cta_text
                                    features_title
                                    features {
                                        feature
                                        disabled_monthly
                                        is_highlighted
                                        tooltip_text
                                    }
                                    annual_cost
                                    monthly_cost
                                    monthly_billing_screen_reader_alt
                                    annual_billing_screen_reader_alt
                                    monthly_cost_subtext_extra
                                    annual_cost_subtext_extra
                                    footnote
                                    monthly_billed_cost
                                    annually_billed_cost
                                    monthly_billed_user_cost
                                    annually_billed_user_cost
                                    cta_variant
                                    free_users_included
                                    cashback_text {
                                        richText
                                    }
                                }
                            }
                        }
                    }
                }
            }
        }
    }
`

export default Pricing
